import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DASHBOARD_MENU_URL } from '../../config/navItems';
import Sidebar from './Sidebar';
import Topbar from './Topbar';

/**
 * 관리자 화면 공용 레이아웃 + 접근 제어.
 *
 * menuUrl 을 넘기면(각 페이지의 <AdminLayout menuUrl="/admin/xxx"> 참고) 로그인한 역할이 그
 * 화면에 대한 메뉴 권한이 있는지까지 검사한다 - 지금까지는 GNB(Sidebar)에서만 안 보이게 숨기고
 * 있었을 뿐이라, 권한이 없는 화면도 주소를 직접 입력하면 그대로 열리고 안에서 API 가 개별적으로
 * 403 을 반환해 화면이 깨져 보이는 문제가 있었다(역할 경계 정리 + UI 사용성 개선).
 *
 * 대시보드(DASHBOARD_MENU_URL)는 메뉴 권한 설정과 무관하게 항상 허용한다 - 로그인 직후 이동할
 * 곳이 없어 리다이렉트 루프에 빠지는 것을 막기 위한 안전장치.
 */
export default function AdminLayout({ title, menuUrl, children }) {
  const { isAuthenticated, canAccess } = useAuth();
  const location = useLocation();
  const deniedAlertShown = useRef(false);
  // 모바일 사이드바(off-canvas 드로어) 열림 상태. 화면 이동 시 AdminLayout 이 새로 그려지며 자동으로 닫힌다.
  const [navOpen, setNavOpen] = useState(false);

  const alwaysAllowed = !menuUrl || menuUrl === DASHBOARD_MENU_URL;
  const access = alwaysAllowed ? true : canAccess(menuUrl);

  useEffect(() => {
    if (access === false && !deniedAlertShown.current) {
      deniedAlertShown.current = true;
      window.alert('접근 권한이 없는 화면입니다.');
    }
  }, [access]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (access === false) {
    return <Navigate to="/dashboard" replace state={{ from: location.pathname }} />;
  }

  if (access === null) {
    // 권한 조회 중 - 허용 안 된 화면이 잠깐이라도 보이지 않도록 Sidebar/콘텐츠 대신 로딩만
    // 표시한다(기존 페이지들이 데이터 로딩에 쓰는 .loading-state 재사용).
    return <div className="loading-state">불러오는 중...</div>;
  }

  return (
    <div className={`app-shell${navOpen ? ' nav-open' : ''}`}>
      <Sidebar />
      {/* 모바일 드로어 열렸을 때 뒤 배경을 덮는 반투명 백드롭(클릭 시 닫힘) */}
      <div className="nav-backdrop" onClick={() => setNavOpen(false)} />
      <div className="main-area">
        <Topbar title={title} onMenuClick={() => setNavOpen((v) => !v)} />
        <div className="content">
          {children}
        </div>
      </div>
    </div>
  );
}
